/**
 * Template Execution Service V2
 * 
 * A simplified, focused approach to template execution that:
 * 1. Only processes the fields specified in the template
 * 2. Uses field-specific system prompts
 * 3. Provides clear progress tracking and error handling
 * 
 * This file serves as the execution engine for the V2 template system.
 */
import { 
  prepareFilteredProductData,
  processTemplateResult 
} from './templateProcessingServiceV2';
import { processTemplateWithOpenAI } from './openaiServiceV2';
import { trackTemplateUsage } from './templateUsageService';
import { getTemplateById } from './templateService';
import { getUserModelSettings } from './aiModelService';
import { deductCreditsForAIModel } from './creditService';
import { refundCredits } from './optimizedCreditService';

/**
 * Helper function to get the current value for a variant field, considering localEdits
 * @param {string} productId - The product ID
 * @param {string} variantId - The variant ID
 * @param {string} field - The field name
 * @param {any} originalValue - The original value from the product data
 * @returns {any} The current value (from localEdits if available, otherwise original)
 */
const getCurrentVariantValue = (productId, variantId, field, originalValue) => {
  if (typeof window === 'undefined' || !window.localEdits) {
    return originalValue;
  }
  
  try {
    // Check if there's an updated value in localEdits
    if (
      window.localEdits[productId] && 
      window.localEdits[productId].variant_updates && 
      window.localEdits[productId].variant_updates[variantId] && 
      window.localEdits[productId].variant_updates[variantId][field] !== undefined
    ) {
      const updatedValue = window.localEdits[productId].variant_updates[variantId][field];
      return updatedValue !== null ? updatedValue : originalValue;
    }
  } catch (error) {
    console.error('Error getting current variant value:', error);
  }
  
  return originalValue;
};

/**
 * Process a tool template without using AI
 * @param {Object} template - The template to process
 * @param {Object} productData - The product data to process
 * @returns {Object} The processing result
 */
const processToolTemplate = async (template, productData) => {
  console.log('Processing tool template:', template.name);
  
  // For tool templates, we'll execute the appropriate tool function directly
  // No need to call AI API for tool templates
  // Parse the prompt to determine which tool to execute
  // Format: tool:toolName|parameters
  let result = {};
  
  if (!template.prompt.startsWith('tool:')) {
    return { error: 'Invalid tool template format. Prompt should start with "tool:"' };
  }
  
  // Parse the tool name and parameters
  console.log('Full template prompt:', template.prompt);
  const promptParts = template.prompt.substring(5).split('|');
  console.log('Prompt parts after splitting:', promptParts);
  
  const toolPart = promptParts[0];
  // Join all remaining parts with | to preserve any | characters in the parameters
  const paramsPart = promptParts.slice(1).join('|');
  
  const toolName = toolPart.trim();
  const params = paramsPart ? paramsPart.trim() : '';
  
  console.log('Parsed tool parts:', { toolPart, paramsPart, toolName, params });
  
  console.log('Executing tool template:', { toolName, params });
  
  try {
    // Execute the appropriate tool based on the tool name
    switch (toolName) {
      case 'image_alt_text':
        // Generate image alt text from product title
        const altTextSuffix = params || ' - product image';
        // Use the full product data to get the title
        const productTitle = productData?.fullProduct?.title || productData?.title || productData?.id?.split('/').pop() || 'Product';
        result.alt_text = productTitle + altTextSuffix;
        console.log('Tool executed: Generated image alt text', {
          productTitle,
          suffix: altTextSuffix,
          result: result.alt_text
        });
        break;
        
      case 'vendor_name':
        // Set vendor name
        result.vendor = params;
        console.log('Tool executed: Set vendor name', {
          vendorName: params,
          result: result.vendor
        });
        break;
      
      case 'generate_skus':
        // Generate SKUs for variants
        const skuParams = {};
        console.log('Raw params string for SKU generation:', params);
        
        if (params) {
          const paramParts = params.split('|');
          console.log('Param parts after splitting:', paramParts);
          
          paramParts.forEach(param => {
            console.log('Processing param part:', param);
            const [key, value] = param.split('=');
            console.log('Key-value pair:', { key, value });
            
            if (key && value !== undefined) {
              skuParams[key.trim()] = value.trim();
              console.log(`Added parameter ${key.trim()} = ${value.trim()}`);
            }
          });
        }
        
        console.log('Final parsed SKU parameters:', skuParams);
        
        // Get prefix and suffix from parameters
        const prefix = skuParams.prefix || '';
        const suffix = skuParams.suffix || '';
        
        console.log('SKU parameters:', { params, skuParams, prefix, suffix });
        
        // Get the product data from either the full product or the filtered data
        const fullProduct = productData.fullProduct || productData;
        
        // Make sure we have a valid product title
        if (!fullProduct.title) {
          return { error: 'Product title is required to generate SKUs' };
        }
        
        // Helper function to slugify text for SKUs (same as in ProductTools.js)
        const slugifyForSku = (text) => {
          if (!text) return '';
          
          return text
            .toLowerCase()
            .replace(/[^\w\s-]/g, '') // Remove special characters
            .replace(/\s+/g, '-')     // Replace spaces with hyphens
            .replace(/-+/g, '-')      // Replace multiple hyphens with single hyphen
            .replace(/^-+|-+$/g, ''); // Trim hyphens from start and end
        };
        
        // Get first 2 words of the title and slugify (same as in ProductTools.js)
        const getFirstNWords = (text, n) => {
          if (!text) return '';
          const words = text.split(/\s+/);
          return words.slice(0, n).join(' ');
        };
        
        const shortTitle = getFirstNWords(fullProduct.title, 2);
        const slugifiedTitle = slugifyForSku(shortTitle);
        
        // Create SKUs for each variant
        result.variants = {};
        
        // Get variants from the full product data
        const variants = fullProduct.variants || [];
        
        // Debug log to see the variant structure
        console.log('First variant data:', variants.length > 0 ? JSON.stringify(variants[0], null, 2) : 'No variants');
        
        if (variants.length > 0) {
          variants.forEach(variant => {
            // Debug log for each variant
            console.log('Processing variant:', {
              id: variant.id,
              title: variant.title,
              option1: variant.option1,
              option2: variant.option2,
              option3: variant.option3,
              options: variant.options
            });
            
            // Get variant options - try different approaches to access options
            const optionValues = [];
            
            // Try direct option properties first
            if (variant.option1) optionValues.push(slugifyForSku(variant.option1));
            if (variant.option2) optionValues.push(slugifyForSku(variant.option2));
            if (variant.option3) optionValues.push(slugifyForSku(variant.option3));
            
            // If no options found, try the options array if available
            if (optionValues.length === 0 && variant.options && Array.isArray(variant.options)) {
              variant.options.forEach(option => {
                if (option) optionValues.push(slugifyForSku(option));
              });
            }
            
            // If still no options, try to extract from variant title
            if (optionValues.length === 0 && variant.title) {
              const titleParts = variant.title.split(' / ');
              if (titleParts.length > 1) { // First part is usually the product title
                titleParts.forEach(part => {
                  optionValues.push(slugifyForSku(part));
                });
              }
            }
            
            const optionValuesString = optionValues.join('-');
            console.log('Option values for variant:', {
              variantId: variant.id,
              optionValues,
              optionValuesString
            });
            
            // Build the SKU with optional prefix and suffix (same pattern as ProductTools.js)
            let newSku = '';
            
            // Add prefix if exists
            if (prefix) {
              newSku += prefix;
            }
            
            // Add slugified title if exists
            if (slugifiedTitle) {
              if (newSku && !newSku.endsWith('-')) newSku += '-';
              newSku += slugifiedTitle;
            }
            
            // Add option values if exist
            if (optionValuesString) {
              if (newSku && !newSku.endsWith('-')) newSku += '-';
              newSku += optionValuesString;
            }
            
            // Add suffix if exists
            if (suffix) {
              console.log('Adding suffix to SKU:', { suffix, currentSku: newSku });
              if (newSku && !newSku.endsWith('-')) newSku += '-';
              newSku += suffix;
              console.log('SKU after adding suffix:', newSku);
            } else {
              console.log('No suffix to add to SKU');
            }
            
            // Ensure SKU doesn't exceed 255 characters
            if (newSku.length > 255) {
              newSku = newSku.substring(0, 255);
            }
            
            result.variants[variant.id] = { sku: newSku };
          });
        }
        
        console.log('Tool executed: Generated SKUs', {
          prefix,
          suffix,
          productTitle: fullProduct.title,
          variantCount: Object.keys(result.variants || {}).length
        });
        break;
        
      case 'generate_handles':
        // Get the product data from either the full product or the filtered data
        const productForHandle = productData.fullProduct || productData;
        
        // Make sure we have a valid product title
        if (!productForHandle.title) {
          return { error: 'Product title is required to generate handle' };
        }
        
        // Generate handle from title
        const handle = productForHandle.title
          .toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/[\s_-]+/g, '-')
          .replace(/^-+|-+$/g, '');
        
        result.handle = handle;
        console.log('Tool executed: Generated handle from title', {
          productTitle: productForHandle.title,
          handle
        });
        break;
        
      case 'handle_prefix_suffix':
        // Add prefix/suffix to handle
        const handleParams = {};
        if (params) {
          params.split('|').forEach(param => {
            const [key, value] = param.split('=');
            if (key && value !== undefined) {
              handleParams[key.trim()] = value.trim();
            }
          });
        }
        
        // Get the product data from either the full product or the filtered data
        const productForHandlePS = productData.fullProduct || productData;
        
        const handlePrefix = handleParams.prefix || '';
        const handleSuffix = handleParams.suffix || '';
        
        // Get current handle or generate from title if empty
        let currentHandle = productForHandlePS.handle;
        if (!currentHandle) {
          // Make sure we have a valid product title
          if (!productForHandlePS.title) {
            return { error: 'Product title is required to generate handle' };
          }
          
          currentHandle = productForHandlePS.title
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
        }
        
        result.handle = `${handlePrefix}${currentHandle}${handleSuffix}`;
        console.log('Tool executed: Added prefix/suffix to handle', {
          originalHandle: currentHandle,
          prefix: handlePrefix,
          suffix: handleSuffix,
          newHandle: result.handle
        });
        break;
        
      case 'update_prices':
        // Update prices based on parameters
        const priceParams = {};
        if (params) {
          params.split('|').forEach(param => {
            const [key, value] = param.split('=');
            if (key && value !== undefined) {
              priceParams[key.trim()] = value.trim();
            }
          });
        }
        
        // Get the product data from either the full product or the filtered data
        const productForPrices = productData.fullProduct || productData;
        
        const amount = parseFloat(priceParams.amount || '0');
        const changeType = priceParams.type || 'percentage';
        const priceField = priceParams.field || 'price';
        
        // Create updates for each variant
        result.variants = {};
        
        // Get variants from the full product data
        const variantsForPrices = productForPrices.variants || [];
        
        // Create a simple array of price updates in the same format as the PricingTools component
        const priceUpdates = [];
        
        if (variantsForPrices.length > 0) {
          variantsForPrices.forEach(variant => {
            // Get the correct field name and current value based on the priceField parameter
            let currentFieldValue = 0;
            let fieldName = priceField;
            
            // Handle compareAtPrice field correctly
            if (priceField === 'compareAtPrice' || priceField === 'compare_at_price') {
              // Normalize field name for variant data access
              fieldName = variant.compareAtPrice !== undefined ? 'compareAtPrice' : 'compare_at_price';
              currentFieldValue = parseFloat(variant[fieldName] || variant.price || 0);
            } else {
              // Regular price field
              currentFieldValue = parseFloat(variant[priceField] || 0);
            }
            
            // Calculate new price based on change type
            let newPrice = currentFieldValue;
            if (changeType === 'percentage') {
              // Percentage change
              newPrice = currentFieldValue * (1 + amount / 100);
            } else {
              // Fixed amount change
              newPrice = currentFieldValue + amount;
            }
            
            // Ensure price is not negative
            newPrice = Math.max(0, newPrice);
            
            // Format the price with 2 decimal places
            const formattedPrice = parseFloat(newPrice.toFixed(2));
            
            // Determine the correct field name for the result
            const resultFieldName = priceField === 'compareAtPrice' ? 'compare_at_price' : priceField;
            
            // Add to result for the template execution system
            result.variants[variant.id] = { [resultFieldName]: formattedPrice.toFixed(2) };
            
            // Add to price updates array with the correct field name for local edits
            priceUpdates.push({
              productId: productData.id,
              variantId: variant.id,
              field: priceField === 'compareAtPrice' ? 'compare_at_price' : priceField,
              value: formattedPrice.toFixed(2)
            });
          });
        }
        
        // SIMPLE DIRECT UPDATE: Apply price updates directly to window.localEdits
        // This uses the EXACT SAME approach as the manual price tools in handlePriceUpdate
        if (typeof window !== 'undefined' && window.localEdits && priceUpdates.length > 0) {
          try {
            priceUpdates.forEach(update => {
              const { productId, variantId, field, value } = update;
              
              // Initialize product in localEdits if needed
              if (!window.localEdits[productId]) {
                window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
              }
              
              // Initialize variant_updates if needed
              if (!window.localEdits[productId].variant_updates) {
                window.localEdits[productId].variant_updates = {};
              }
              
              // Initialize variant in localEdits if needed
              if (!window.localEdits[productId].variant_updates[variantId]) {
                window.localEdits[productId].variant_updates[variantId] = {};
              }
              
              // Update the specific field - EXACTLY as done in handlePriceUpdate
              const fieldName = field === 'compareAtPrice' ? 'compare_at_price' : field;
              window.localEdits[productId].variant_updates[variantId][fieldName] = 
                value !== null ? value.toString() : null;
              
              // Update timestamp to force re-render
              window.localEdits[productId]._timestamp = Date.now();
              
              console.log('Updated variant in localEdits:', {
                productId,
                variantId,
                field: fieldName,
                value,
                variant: window.localEdits[productId].variant_updates[variantId]
              });
            });
            
            // Set hasUnsavedChanges flag if it exists
            if (window.setHasUnsavedChanges && typeof window.setHasUnsavedChanges === 'function') {
              window.setHasUnsavedChanges(true);
            }
          } catch (error) {
            console.error('Error directly updating prices in window.localEdits:', error);
          }
        }
        
        console.log('Tool executed: Updated prices', {
          amount,
          changeType,
          priceField,
          variantCount: Object.keys(result.variants || {}).length,
          updates: priceUpdates
        });
        break;
        
      case 'sales_price_factor':
        // Apply sales price factor
        const factor = parseFloat(params || '1.0');
        
        // Get the product data from either the full product or the filtered data
        const productForFactor = productData.fullProduct || productData;
        
        // Create updates for each variant
        result.variants = {};
        const factorUpdates = [];
        
        // Get variants from the full product data
        const variantsForFactor = productForFactor.variants || [];
        
        if (variantsForFactor.length > 0) {
          variantsForFactor.forEach(variant => {
            // Get the current price from localEdits if available, otherwise use the original price
            const originalPrice = parseFloat(variant.price || 0);
            const currentPrice = parseFloat(
              getCurrentVariantValue(productForFactor.id, variant.id, 'price', originalPrice)
            );
            
            // We're not changing the regular price, just using it to calculate the compare-at price
            
            // Get the current compare-at price from localEdits if available, otherwise use the original
            const originalComparePrice = parseFloat(variant.compare_at_price || 0);
            const currentComparePrice = parseFloat(
              getCurrentVariantValue(productForFactor.id, variant.id, 'compare_at_price', originalComparePrice)
            );
            
            // Calculate the new compare-at price based on the factor
            // Set the compare price to be the factor of the current price
            let compareAtValue = null;
            const compareAtPrice = currentPrice * factor;
            compareAtValue = parseFloat(compareAtPrice.toFixed(2));
            
            // Add to result for the template execution system - only updating compare_at_price
            result.variants[variant.id] = { 
              compare_at_price: compareAtValue !== null ? compareAtValue.toFixed(2) : null
            };
            
            // Add compare price update to the updates array
            factorUpdates.push({
              productId: productForFactor.id,
              variantId: variant.id,
              field: 'compare_at_price',
              value: compareAtValue.toFixed(2)
            });
          });
        }
        
        // Apply updates directly to window.localEdits using the same approach as price updates
        if (typeof window !== 'undefined' && window.localEdits && factorUpdates.length > 0) {
          try {
            factorUpdates.forEach(update => {
              const { productId, variantId, field, value } = update;
              
              // Initialize product in localEdits if needed
              if (!window.localEdits[productId]) {
                window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
              }
              
              // Initialize variant_updates if needed
              if (!window.localEdits[productId].variant_updates) {
                window.localEdits[productId].variant_updates = {};
              }
              
              // Initialize variant in localEdits if needed
              if (!window.localEdits[productId].variant_updates[variantId]) {
                window.localEdits[productId].variant_updates[variantId] = {};
              }
              
              // Update the specific field - EXACTLY as done in handlePriceUpdate
              window.localEdits[productId].variant_updates[variantId][field] = 
                value !== null ? value.toString() : null;
              
              // Update timestamp to force re-render
              window.localEdits[productId]._timestamp = Date.now();
              
              console.log('Updated price with factor in localEdits:', {
                productId,
                variantId,
                field,
                value,
                variant: window.localEdits[productId].variant_updates[variantId]
              });
            });
            
            // Set hasUnsavedChanges flag if it exists
            if (window.setHasUnsavedChanges && typeof window.setHasUnsavedChanges === 'function') {
              window.setHasUnsavedChanges(true);
            }
          } catch (error) {
            console.error('Error directly updating prices with factor in window.localEdits:', error);
          }
        }
        
        console.log('Tool executed: Applied sales price factor to compare-at price', {
          factor,
          variantCount: Object.keys(result.variants || {}).length,
          updates: factorUpdates
        });
        break;
        
      case 'round_prices':
        // Round prices to nearest dollar plus cents value
        const cents = parseFloat(params || '0.99');
        
        // Get the product data from either the full product or the filtered data
        const productForRounding = productData.fullProduct || productData;
        
        // Create updates for each variant
        result.variants = {};
        
        // Get variants from the full product data
        const variantsForRounding = productForRounding.variants || [];
        
        // Create a simple array of price updates in the same format as the PricingTools component
        const roundingUpdates = [];
        
        if (variantsForRounding.length > 0) {
          variantsForRounding.forEach(variant => {
            // Get the current price from localEdits if available, otherwise use the original price
            const originalPrice = parseFloat(variant.price || 0);
            const currentPrice = parseFloat(
              getCurrentVariantValue(productForRounding.id, variant.id, 'price', originalPrice)
            );
            
            // Round the regular price
            const dollarPart = Math.floor(currentPrice);
            const newPrice = dollarPart + cents;
            const formattedPrice = parseFloat(newPrice.toFixed(2));
            
            // Get the current compare-at price from localEdits if available, otherwise use the original
            const originalComparePrice = parseFloat(variant.compare_at_price || 0);
            const currentComparePrice = parseFloat(
              getCurrentVariantValue(productForRounding.id, variant.id, 'compare_at_price', originalComparePrice)
            );
            
            // Round the compare-at price if it exists
            let formattedComparePrice = null;
            if (!isNaN(currentComparePrice) && currentComparePrice > 0) {
              const compareDollarPart = Math.floor(currentComparePrice);
              const newComparePrice = compareDollarPart + cents;
              formattedComparePrice = parseFloat(newComparePrice.toFixed(2));
            }
            
            // Add to result for the template execution system
            result.variants[variant.id] = { 
              price: formattedPrice.toFixed(2),
              compare_at_price: formattedComparePrice !== null ? formattedComparePrice.toFixed(2) : null
            };
            
            // Add regular price update to the updates array
            roundingUpdates.push({
              productId: productForRounding.id,
              variantId: variant.id,
              field: 'price',
              value: formattedPrice.toFixed(2)
            });
            
            // Add compare price update to the updates array if it exists
            if (formattedComparePrice !== null) {
              roundingUpdates.push({
                productId: productForRounding.id,
                variantId: variant.id,
                field: 'compare_at_price',
                value: formattedComparePrice.toFixed(2)
              });
            }
          });
        }
        
        // Apply updates directly to window.localEdits using the same approach as price updates
        if (typeof window !== 'undefined' && window.localEdits && roundingUpdates.length > 0) {
          try {
            roundingUpdates.forEach(update => {
              const { productId, variantId, field, value } = update;
              
              // Initialize product in localEdits if needed
              if (!window.localEdits[productId]) {
                window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
              }
              
              // Initialize variant_updates if needed
              if (!window.localEdits[productId].variant_updates) {
                window.localEdits[productId].variant_updates = {};
              }
              
              // Initialize variant in localEdits if needed
              if (!window.localEdits[productId].variant_updates[variantId]) {
                window.localEdits[productId].variant_updates[variantId] = {};
              }
              
              // Update the specific field - EXACTLY as done in handlePriceUpdate
              window.localEdits[productId].variant_updates[variantId][field] = 
                value !== null ? value.toString() : null;
              
              // Update timestamp to force re-render
              window.localEdits[productId]._timestamp = Date.now();
              
              console.log('Updated rounded price in localEdits:', {
                productId,
                variantId,
                field,
                value,
                variant: window.localEdits[productId].variant_updates[variantId]
              });
            });
            
            // Set hasUnsavedChanges flag if it exists
            if (window.setHasUnsavedChanges && typeof window.setHasUnsavedChanges === 'function') {
              window.setHasUnsavedChanges(true);
            }
          } catch (error) {
            console.error('Error directly updating rounded prices in window.localEdits:', error);
          }
        }
        
        console.log('Tool executed: Rounded prices', {
          cents,
          variantCount: Object.keys(result.variants || {}).length,
          updates: roundingUpdates
        });
        break;
        
      case 'compare_price_discount':
        // Set compare-at price discount
        const discountPercent = parseFloat(params || '20');
        
        // Get the product data from either the full product or the filtered data
        const productForCompare = productData.fullProduct || productData;
        
        // Create updates for each variant
        result.variants = {};
        const compareUpdates = [];
        
        // Get variants from the full product data
        const variantsForCompare = productForCompare.variants || [];
        
        if (variantsForCompare.length > 0) {
          variantsForCompare.forEach(variant => {
            // Get the current price from localEdits if available, otherwise use the original price
            const originalPrice = parseFloat(variant.price || 0);
            const currentPrice = parseFloat(
              getCurrentVariantValue(productForCompare.id, variant.id, 'price', originalPrice)
            );
            
            let compareAtValue = null;
            
            if (discountPercent < 100) {
              // Calculate compare-at price based on discount and the CURRENT price
              const compareAtPrice = currentPrice / (1 - discountPercent / 100);
              compareAtValue = compareAtPrice.toFixed(2);
            }
            
            // Add to result for the template execution system
            result.variants[variant.id] = { compare_at_price: compareAtValue };
            
            // Add to updates array for direct application
            compareUpdates.push({
              productId: productForCompare.id,
              variantId: variant.id,
              field: 'compare_at_price',
              value: compareAtValue
            });
          });
        }
        
        // Apply updates directly to window.localEdits using the same approach as price updates
        if (typeof window !== 'undefined' && window.localEdits && compareUpdates.length > 0) {
          try {
            compareUpdates.forEach(update => {
              const { productId, variantId, field, value } = update;
              
              // Initialize product in localEdits if needed
              if (!window.localEdits[productId]) {
                window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
              }
              
              // Initialize variant_updates if needed
              if (!window.localEdits[productId].variant_updates) {
                window.localEdits[productId].variant_updates = {};
              }
              
              // Initialize variant in localEdits if needed
              if (!window.localEdits[productId].variant_updates[variantId]) {
                window.localEdits[productId].variant_updates[variantId] = {};
              }
              
              // Update the specific field - EXACTLY as done in handlePriceUpdate
              window.localEdits[productId].variant_updates[variantId][field] = 
                value !== null ? value.toString() : null;
              
              // Update timestamp to force re-render
              window.localEdits[productId]._timestamp = Date.now();
              
              console.log('Updated compare price in localEdits:', {
                productId,
                variantId,
                field,
                value,
                variant: window.localEdits[productId].variant_updates[variantId]
              });
            });
            
            // Set hasUnsavedChanges flag if it exists
            if (window.setHasUnsavedChanges && typeof window.setHasUnsavedChanges === 'function') {
              window.setHasUnsavedChanges(true);
            }
          } catch (error) {
            console.error('Error directly updating compare prices in window.localEdits:', error);
          }
        }
        
        console.log('Tool executed: Set compare-at price discount', {
          discountPercent,
          variantCount: Object.keys(result.variants || {}).length,
          updates: compareUpdates
        });
        break;
        
      case 'clear_compare_prices':
        // Clear all compare-at prices
        // Get the product data from either the full product or the filtered data
        const productForClear = productData.fullProduct || productData;
        
        // Create updates for each variant
        result.variants = {};
        const clearCompareUpdates = [];
        
        // Get variants from the full product data
        const variantsForClear = productForClear.variants || [];
        
        if (variantsForClear.length > 0) {
          variantsForClear.forEach(variant => {
            // Add to result for the template execution system
            result.variants[variant.id] = { compare_at_price: null };
            
            // Add to updates array for direct application
            clearCompareUpdates.push({
              productId: productForClear.id,
              variantId: variant.id,
              field: 'compare_at_price',
              value: null
            });
          });
        }
        
        // Apply updates directly to window.localEdits using the same approach as price updates
        if (typeof window !== 'undefined' && window.localEdits && clearCompareUpdates.length > 0) {
          try {
            clearCompareUpdates.forEach(update => {
              const { productId, variantId, field, value } = update;
              
              // Initialize product in localEdits if needed
              if (!window.localEdits[productId]) {
                window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
              }
              
              // Initialize variant_updates if needed
              if (!window.localEdits[productId].variant_updates) {
                window.localEdits[productId].variant_updates = {};
              }
              
              // Initialize variant in localEdits if needed
              if (!window.localEdits[productId].variant_updates[variantId]) {
                window.localEdits[productId].variant_updates[variantId] = {};
              }
              
              // Update the specific field - EXACTLY as done in handlePriceUpdate
              window.localEdits[productId].variant_updates[variantId][field] = null;
              
              // Update timestamp to force re-render
              window.localEdits[productId]._timestamp = Date.now();
              
              console.log('Cleared compare price in localEdits:', {
                productId,
                variantId,
                field,
                variant: window.localEdits[productId].variant_updates[variantId]
              });
            });
            
            // Set hasUnsavedChanges flag if it exists
            if (window.setHasUnsavedChanges && typeof window.setHasUnsavedChanges === 'function') {
              window.setHasUnsavedChanges(true);
            }
          } catch (error) {
            console.error('Error directly clearing compare prices in window.localEdits:', error);
          }
        }
        
        console.log('Tool executed: Cleared compare-at prices', {
          variantCount: Object.keys(result.variants || {}).length,
          updates: clearCompareUpdates
        });
        break;
        
      case 'set_inventory':
        // Set inventory quantity
        const inventoryParams = {};
        if (params) {
          params.split('|').forEach(param => {
            const [key, value] = param.split('=');
            if (key && value !== undefined) {
              inventoryParams[key.trim()] = value.trim();
            }
          });
        }
        
        const quantity = parseInt(inventoryParams.quantity || '0');
        const ignoreZero = inventoryParams.ignoreZero === 'yes';
        
        // Get the product data from either the full product or the filtered data
        const productForInventory = productData.fullProduct || productData;
        
        // Create updates for each variant
        result.variants = {};
        const inventoryUpdates = [];
        
        // Get variants from the full product data
        const variantsForInventory = productForInventory.variants || [];
        
        if (variantsForInventory.length > 0) {
          variantsForInventory.forEach(variant => {
            // Get the current inventory from localEdits if available, otherwise use the original inventory
            const originalInventory = parseInt(variant.inventory_quantity || 0);
            const currentInventory = parseInt(
              getCurrentVariantValue(productForInventory.id, variant.id, 'inventory_quantity', originalInventory)
            );
            
            // Skip variants with 0 inventory if ignoreZero is true
            if (ignoreZero && currentInventory === 0) {
              return;
            }
            
            // Add to result for the template execution system
            result.variants[variant.id] = { inventory_quantity: quantity };
            
            // Add to inventory updates array for direct application
            inventoryUpdates.push({
              productId: productData.id,
              variantId: variant.id,
              field: 'inventory_quantity',
              value: quantity.toString()
            });
          });
        }
        
        // Apply updates directly to window.localEdits using the same approach as price updates
        if (typeof window !== 'undefined' && window.localEdits && inventoryUpdates.length > 0) {
          try {
            inventoryUpdates.forEach(update => {
              const { productId, variantId, field, value } = update;
              
              // Initialize product in localEdits if needed
              if (!window.localEdits[productId]) {
                window.localEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
              }
              
              // Initialize variant_updates if needed
              if (!window.localEdits[productId].variant_updates) {
                window.localEdits[productId].variant_updates = {};
              }
              
              // Initialize variant in localEdits if needed
              if (!window.localEdits[productId].variant_updates[variantId]) {
                window.localEdits[productId].variant_updates[variantId] = {};
              }
              
              // Update the specific field - EXACTLY as done in handlePriceUpdate
              window.localEdits[productId].variant_updates[variantId][field] = 
                value !== null ? value.toString() : null;
              
              // Update timestamp to force re-render
              window.localEdits[productId]._timestamp = Date.now();
              
              console.log('Updated inventory in localEdits:', {
                productId,
                variantId,
                field,
                value,
                variant: window.localEdits[productId].variant_updates[variantId]
              });
            });
            
            // Set hasUnsavedChanges flag if it exists
            if (window.setHasUnsavedChanges && typeof window.setHasUnsavedChanges === 'function') {
              window.setHasUnsavedChanges(true);
            }
          } catch (error) {
            console.error('Error directly updating inventory in window.localEdits:', error);
          }
        }
        
        console.log('Tool executed: Set inventory quantity', {
          quantity,
          ignoreZero,
          variantCount: Object.keys(result.variants || {}).length
        });
        break;
        
      default:
        return { error: `Unknown tool: ${toolName}` };
    }
    
    return result;
  } catch (error) {
    console.error('Error executing tool template:', error);
    return { error: `Error executing tool: ${error.message}` };
  }
};

/**
 * Execute templates on products
 * @param {Array} templateIds - The IDs of templates to apply
 * @param {Array} products - The products to process
 * @param {Array} templates - The templates to apply (optional, will load by ID if not provided)
 * @param {Object} options - Execution options
 * @returns {Object} The execution results
 */
export const executeTemplates = async (templateIds, products, templates = [], options = {}) => {
  const {
    userId,
    shopId,
    onProgress,
    onComplete,
    getFieldValue
  } = options;
  
  console.log('%c[Template Execution Start]', 'color: #0099ff; font-weight: bold', {
    templateIds,
    templateCount: templateIds.length,
    productCount: products.length,
    productsData: products.map(p => ({ id: p.id, title: p.title })),
    userId,
    shopId,
    hasGetFieldValue: !!getFieldValue
  });
  
  // Initialize results
  const results = {};
  const latestUpdates = {};
  
  // Initialize progress tracking
  const totalOperations = templateIds.length * products.length;
  let completedOperations = 0;
  
  // Load templates if not provided
  const loadedTemplates = [...templates];
  
  // Load any missing templates by ID
  for (const templateId of templateIds) {
    if (!loadedTemplates.find(t => t.id === templateId)) {
      try {
        console.log('Loading template by ID:', templateId);
        const template = await getTemplateById(templateId);
        console.log('Successfully loaded template:', template?.name);
        console.log('Template prompt:', template?.prompt);
        
        if (template) {
          loadedTemplates.push(template);
        } else {
          console.error(`Template not found: ${templateId}`);
        }
      } catch (error) {
        console.error(`Error loading template ${templateId}:`, error);
      }
    }
  }
  
  console.log('Loaded templates:', loadedTemplates.map(t => ({ id: t.id, name: t.name })));
  
  if (loadedTemplates.length === 0) {
    console.error('No templates were loaded. Cannot proceed with execution.');
    return {};
  }
  
  // Check if all templates are tool templates
  const allToolTemplates = loadedTemplates.every(template => template.type === 'tool');
  
  // If all templates are tool templates, we can skip loading AI model settings
  let userModelSettings = null;
  if (userId && !allToolTemplates) {
    try {
      userModelSettings = await getUserModelSettings(userId);
      console.log('Loaded user model settings:', userModelSettings);
      
      // Set default base URLs if not provided
      if (!userModelSettings) {
        userModelSettings = {
          openai: { baseUrl: '/api/openai' },
          deepseek: { baseUrl: 'https://api.deepseek.com/v1' },
          gemini: { baseUrl: 'https://generativelanguage.googleapis.com/v1beta' }
        };
        console.log('Using default model settings:', userModelSettings);
      } else {
        // Ensure the openai settings exist and have a baseUrl
        if (!userModelSettings.openai) {
          userModelSettings.openai = { baseUrl: '/api/openai' };
        } else if (!userModelSettings.openai.baseUrl) {
          userModelSettings.openai.baseUrl = '/api/openai';
        }
        
        console.log('Using configured model settings with defaults applied:', userModelSettings);
      }
    } catch (error) {
      console.error('Error loading user model settings:', error);
      // Create default settings
      userModelSettings = {
        openai: { baseUrl: '/api/openai' },
        deepseek: { baseUrl: 'https://api.deepseek.com/v1' },
        gemini: { baseUrl: 'https://generativelanguage.googleapis.com/v1beta' }
      };
      console.log('Using fallback model settings due to error:', userModelSettings);
    }
  } else if (allToolTemplates) {
    console.log('All templates are tool templates, skipping AI model settings loading');
  }
  
  // Track template usage
  if (userId && shopId) {
    try {
      // Track usage for each template
      for (const templateId of templateIds) {
        const template = loadedTemplates.find(t => t.id === templateId);
        const templateType = template?.type || 'product';
        
        console.log('%c[Tracking Template Usage]', 'color: #cc9900; font-weight: bold', {
          templateId,
          templateName: template?.name,
          templateType
        });
        
        // Fix parameter order: userId, templateId, templateType, shopId
        await trackTemplateUsage(userId, templateId, templateType, shopId);
      }
    } catch (error) {
      console.error('Error tracking template usage:', error);
      // Continue execution even if tracking fails
    }
  }
  
  try {
    // Process each product
    for (const product of products) {
      const productId = product.id || 'unknown';
      
      // Initialize product results
      results[productId] = {};
      latestUpdates[productId] = {};
      
      console.log('%c[Processing Product]', 'color: #6600cc; font-weight: bold', {
        productId,
        title: product.title,
        hasId: !!product.id,
        productData: JSON.stringify(product).substring(0, 200) + '...'
      });
      
      // Process each template
      for (const templateId of templateIds) {
        // Find the template
        const template = loadedTemplates.find(t => t.id === templateId);
        if (!template) {
          console.error(`Template not found: ${templateId}`);
          continue;
        }
        
        console.log('%c[Processing Template]', 'color: #009966; font-weight: bold', {
          templateId,
          templateName: template.name,
          inputFields: template.inputFields,
          outputFields: template.outputFields,
          inputMetafields: template.inputMetafields,
          outputMetafields: template.outputMetafields,
          modelId: template.modelId
        });
        
        try {
          // Prepare filtered product data based on template input fields
          const filteredProductData = prepareFilteredProductData(product, template, getFieldValue);
          
          console.log('Filtered product data:', filteredProductData);
          
          let templateResult;
          
          // Check if this is a tool template
          if (template.type === 'tool') {
            console.log('Processing tool template:', template.name);
            
            // For tool templates, we'll execute the appropriate tool function directly
            // No need to call AI API for tool templates
            // Pass both the filtered data and the full product to ensure we have access to all needed fields
            templateResult = await processToolTemplate(template, { ...filteredProductData, fullProduct: product });
          } else {
            // Process the template with the selected AI model
            console.log('Processing template with AI model:', template.modelId || 'default');
            templateResult = await processTemplateWithOpenAI(
              template,
              filteredProductData,
              null, // No longer passing apiKey directly
              { 
                userId, 
                userModelSettings,
                modelId: template.modelId // Pass the modelId from the template
              }
            );
          }
          
          console.log('Template result:', templateResult);
          
          // Check if there was an error in the template result
          if (templateResult.error) {
            console.error(`Error in template result for ${template.name}:`, templateResult.error);
            
            // Store the error in the results
            results[productId][templateId] = {
              error: templateResult.error,
              metafields: {}
            };
          } else {
            // Process the template result
            const processedResult = processTemplateResult(templateResult, product, template);
            
            console.log('Processed template result:', processedResult);
            
            // Special handling for tool templates with variants
            if (template.type === 'tool' && templateResult.variants && Object.keys(templateResult.variants).length > 0) {
              console.log('%c[Tool Template Variants Detected]', 'color: #ff6600; font-weight: bold', {
                productId,
                templateId,
                variantCount: Object.keys(templateResult.variants).length
              });
              
              // Map variants to variant_skus for consistent processing
              processedResult.variant_skus = templateResult.variants;
            }
            
            // Store the processed result
            results[productId][templateId] = processedResult;
            
            // Update latestUpdates with the most recent changes
            latestUpdates[productId] = {
              ...latestUpdates[productId]
            };
            
            // Add top-level fields to latestUpdates
            const topLevelFields = ['title', 'body_html', 'description', 'vendor', 'product_type', 'tags'];
            for (const field of topLevelFields) {
              if (processedResult[field] !== undefined) {
                latestUpdates[productId][field] = processedResult[field];
              }
            }
            
            // Store template execution metadata for potential refunds
            if (!latestUpdates[productId]._templateExecutions) {
              latestUpdates[productId]._templateExecutions = [];
            }
            
            // Handle variant SKUs if present
            if (processedResult.variant_skus && Object.keys(processedResult.variant_skus).length > 0) {
              console.log('%c[Processing Variant SKUs for Updates]', 'color: #6600cc; font-weight: bold', {
                productId,
                variantCount: Object.keys(processedResult.variant_skus).length
              });
              
              // Initialize variants object if needed
              if (!latestUpdates[productId].variants) {
                latestUpdates[productId].variants = {};
              }
              
              // Apply SKU updates to each variant
              Object.entries(processedResult.variant_skus).forEach(([variantId, variantData]) => {
                // Initialize this variant if needed
                if (!latestUpdates[productId].variants[variantId]) {
                  latestUpdates[productId].variants[variantId] = {};
                }
                
                // Apply the SKU update
                if (variantData.sku) {
                  latestUpdates[productId].variants[variantId].sku = variantData.sku;
                  console.log(`Applied SKU ${variantData.sku} to variant ${variantId}`);
                }
                
                // Apply the price update
                if (variantData.price) {
                  latestUpdates[productId].variants[variantId].price = variantData.price;
                  console.log(`Applied price ${variantData.price} to variant ${variantId}`);
                }
                
                // Apply the compare at price update
                if (variantData.compare_at_price) {
                  latestUpdates[productId].variants[variantId].compare_at_price = variantData.compare_at_price;
                  console.log(`Applied compare at price ${variantData.compare_at_price} to variant ${variantId}`);
                }
              });
              
              // Also update window.localEdits for immediate UI updates
              try {
                if (typeof window !== 'undefined' && window.localEdits && window.localEdits[productId]) {
                  // Initialize variants object if needed
                  if (!window.localEdits[productId].variants) {
                    window.localEdits[productId].variants = {};
                  }
                  
                  // Apply SKU updates to each variant in localEdits
                  Object.entries(processedResult.variant_skus).forEach(([variantId, variantData]) => {
                    // Initialize this variant if needed
                    if (!window.localEdits[productId].variants[variantId]) {
                      window.localEdits[productId].variants[variantId] = {};
                    }
                    
                    // Apply the SKU update
                    if (variantData.sku) {
                      window.localEdits[productId].variants[variantId].sku = variantData.sku;
                      window.localEdits[productId].variants[variantId]._timestamp = Date.now();
                      console.log(`Applied SKU ${variantData.sku} to variant ${variantId} in window.localEdits`);
                    }
                    
                    // Apply the price update
                    if (variantData.price) {
                      window.localEdits[productId].variants[variantId].price = variantData.price;
                      window.localEdits[productId].variants[variantId]._timestamp = Date.now();
                      console.log(`Applied price ${variantData.price} to variant ${variantId} in window.localEdits`);
                    }
                    
                    // Apply the compare at price update
                    if (variantData.compare_at_price) {
                      window.localEdits[productId].variants[variantId].compareAtPrice = variantData.compare_at_price;
                      window.localEdits[productId].variants[variantId]._timestamp = Date.now();
                      console.log(`Applied compare at price ${variantData.compare_at_price} to variant ${variantId} in window.localEdits`);
                    }
                  });
                }
              } catch (error) {
                console.error('Error updating window.localEdits for variant SKUs:', error);
              }
            }
            
            // Add metafields to latestUpdates
            if (processedResult.metafields) {
              // Initialize metafields in latestUpdates if needed
              if (!latestUpdates[productId].metafields) {
                latestUpdates[productId].metafields = {};
              }
              if (!latestUpdates[productId].metafield_updates) {
                latestUpdates[productId].metafield_updates = {};
              }
              if (!latestUpdates[productId].metafields_array) {
                latestUpdates[productId].metafields_array = [];
              }
              
              // Process each metafield
              Object.entries(processedResult.metafields).forEach(([metafieldKey, value]) => {
                // Skip if the value is undefined or null
                if (value === undefined || value === null) return;
                
                // Parse namespace and key from metafieldKey (format: namespace.key)
                const [namespace, key] = metafieldKey.split('.');
                if (!namespace || !key) {
                  console.error(`Invalid metafield key format: ${metafieldKey}`);
                  return;
                }
                
                console.log(`%c[Adding metafield ${metafieldKey}]`, 'color: #4CAF50; font-weight: bold', {
                  value,
                  valueType: typeof value,
                  valueLength: typeof value === 'string' ? value.length : null
                });
                
                // Add to metafields object
                if (!latestUpdates[productId].metafields[metafieldKey]) {
                  latestUpdates[productId].metafields[metafieldKey] = {
                    value: value,
                    _timestamp: Date.now()
                  };
                } else {
                  latestUpdates[productId].metafields[metafieldKey].value = value;
                  latestUpdates[productId].metafields[metafieldKey]._timestamp = Date.now();
                }
                
                // Add to metafield_updates
                latestUpdates[productId].metafield_updates[metafieldKey] = value;
                
                // Check if the metafield already exists in the array
                const existingIndex = latestUpdates[productId].metafields_array.findIndex(
                  m => m.namespace === namespace && m.key === key
                );
                
                if (existingIndex >= 0) {
                  // Update existing metafield
                  latestUpdates[productId].metafields_array[existingIndex].value = value;
                } else {
                  // Add new metafield
                  latestUpdates[productId].metafields_array.push({
                    namespace,
                    key,
                    value,
                    type: 'single_line_text_field' // Default type
                  });
                }
                
                // Update window.localEdits directly for immediate UI updates
                try {
                  if (window.localEdits && window.localEdits[productId]) {
                    // Update metafields object
                    if (!window.localEdits[productId].metafields) {
                      window.localEdits[productId].metafields = {};
                    }
                    window.localEdits[productId].metafields[metafieldKey] = {
                      value: value,
                      _timestamp: Date.now()
                    };
                    
                    // Update metafield_updates
                    if (!window.localEdits[productId].metafield_updates) {
                      window.localEdits[productId].metafield_updates = {};
                    }
                    window.localEdits[productId].metafield_updates[metafieldKey] = value;
                    
                    // Update metafields_array
                    if (!window.localEdits[productId].metafields_array) {
                      window.localEdits[productId].metafields_array = [];
                    }
                    
                    const localExistingIndex = window.localEdits[productId].metafields_array.findIndex(
                      m => m.namespace === namespace && m.key === key
                    );
                    
                    if (localExistingIndex >= 0) {
                      window.localEdits[productId].metafields_array[localExistingIndex].value = value;
                    } else {
                      window.localEdits[productId].metafields_array.push({
                        namespace,
                        key,
                        value,
                        type: 'single_line_text_field' // Default type
                      });
                    }
                    
                    console.log(`%c[Updated window.localEdits for ${metafieldKey}]`, 'color: #2196F3; font-weight: bold', {
                      productId,
                      metafieldKey,
                      value
                    });
                  }
                } catch (error) {
                  console.error('Error updating window.localEdits:', error);
                }
              });
            }
            
            console.log('%c[Template Result Processed]', 'color: #009966; font-weight: bold', {
              templateId,
              templateName: template.name,
              result: processedResult,
              metafieldCount: processedResult.metafields ? Object.keys(processedResult.metafields).length : 0
            });
            
            // Deduct credits for successful template execution
            if (userId) {
              try {
                // Calculate output fields count
                const outputFieldsCount = (template.outputFields || []).length;
                const outputMetafieldsCount = (template.outputMetafields || []).length;
                const totalOutputs = outputFieldsCount + outputMetafieldsCount;
                
                // For tool templates, we don't deduct any credits
                if (template.type === 'tool') {
                  // Create execution metadata with zero credit cost
                  const executionMetadata = {
                    templateId: template.id,
                    templateName: template.name,
                    productId,
                    productTitle: product.title,
                    outputFields: template.outputFields,
                    outputMetafields: template.outputMetafields,
                    totalOutputs,
                    effectiveTotalOutputs: totalOutputs,
                    creditCostPerOutput: 0,
                    totalCreditCost: 0,
                    shopId,
                    executionId: `${template.id}_${productId}_${Date.now()}`
                  };
                  
                  // Store the execution metadata in the results
                  results[productId][templateId].executionMetadata = executionMetadata;
                  
                  // Also store in latestUpdates for tracking
                  if (!latestUpdates[productId]._templateExecutions) {
                    latestUpdates[productId]._templateExecutions = [];
                  }
                  latestUpdates[productId]._templateExecutions.push(executionMetadata);
                  
                  // Track template usage for analytics only (no credit deduction)
                  await trackTemplateUsage(userId, template.id, 'tool', shopId);
                  
                  console.log(`%c[Tool template executed with no credits]`, 'color: #4CAF50; font-weight: bold', {
                    templateId,
                    templateName: template.name,
                    productId
                  });
                } else {
                  // For AI templates, calculate and deduct credits as usual
                  // Ensure at least 1 credit is charged for AI templates
                  const effectiveTotalOutputs = Math.max(totalOutputs, 1);
                  
                  // Get the credit cost per output for this model
                  const creditCostPerOutput = template.creditCostPerOutput || 1;
                  const totalCreditCost = effectiveTotalOutputs * creditCostPerOutput;
                  
                  // Create execution metadata
                  const executionMetadata = {
                    templateId: template.id,
                    templateName: template.name,
                    productId,
                    productTitle: product.title,
                    outputFields: template.outputFields,
                    outputMetafields: template.outputMetafields,
                    totalOutputs,
                    effectiveTotalOutputs,
                    creditCostPerOutput,
                    totalCreditCost,
                    shopId,
                    executionId: `${template.id}_${productId}_${Date.now()}`
                  };
                  
                  // Store the execution metadata in the results
                  results[productId][templateId].executionMetadata = executionMetadata;
                  
                  // Also store in latestUpdates for refunds during push to Shopify
                  if (!latestUpdates[productId]._templateExecutions) {
                    latestUpdates[productId]._templateExecutions = [];
                  }
                  latestUpdates[productId]._templateExecutions.push(executionMetadata);
                  
                  // Deduct credits for AI templates
                  await deductCreditsForAIModel(userId, totalCreditCost, executionMetadata);
                  
                  // Track template usage for analytics and reporting
                  await trackTemplateUsage(userId, template.id, template.type || 'product', shopId);
                  
                  console.log(`%c[Credits deducted for ${template.name}]`, 'color: #FF9800; font-weight: bold', {
                    templateId,
                    templateName: template.name,
                    productId,
                    totalCreditCost,
                    totalOutputs,
                    executionId: executionMetadata.executionId
                  });
                }
              } catch (error) {
                console.error(`Error deducting credits for ${template.name}:`, error);
                // We don't need to refund here since the deduction failed
                // Just log the error and continue
              }
            }
          }
        } catch (error) {
          console.error(`Error processing template ${templateId} for product ${productId}:`, error);
          
          // Store the error
          results[productId][templateId] = {
            error: error.message || 'Unknown error'
          };
        }
        
        // Update progress
        completedOperations++;
        if (onProgress && typeof onProgress === 'function') {
          try {
            onProgress(completedOperations / totalOperations);
          } catch (progressError) {
            console.error('Error in progress callback:', progressError);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error in template execution:', error);
  }
  
  console.log('%c[Template Execution Complete]', 'color: #0099ff; font-weight: bold', {
    totalOperations,
    completedOperations,
    success: completedOperations === totalOperations,
    results: JSON.stringify(results).substring(0, 500) + '...'
  });
  
  // Call onComplete callback if provided
  if (onComplete) {
    onComplete({
      results,
      latestUpdates,
      totalOperations,
      completedOperations,
      success: completedOperations === totalOperations
    });
  }
  
  return results;
};
